import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { MonitorPlay, Copy, ExternalLink, Ban, Link2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShareViewerButton = ({ token }) => {
    const [open, setOpen] = useState(false);
    const [share, setShare] = useState(null);
    const [loading, setLoading] = useState(false);
    const headers = { Authorization: `Bearer ${token}` };

    const fetchStatus = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/viewer/share`, { headers: { Authorization: `Bearer ${token}` } });
            setShare(res.data);
        } catch (err) {
            console.error('Erreur statut partage viewer:', err);
        }
    }, [token]);

    useEffect(() => { if (open) fetchStatus(); }, [open, fetchStatus]);

    const viewerUrl = share?.active ? `${window.location.origin}/viewer/${share.token}` : null;

    const handleActivate = async () => {
        setLoading(true);
        try {
            const res = await axios.post(`${API}/viewer/share`, {}, { headers });
            setShare(res.data);
            toast.success('Lien de partage actif');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setLoading(false);
        }
    };

    const handleRevoke = async () => {
        if (!window.confirm('Révoquer le lien de partage ? Toutes les personnes utilisant ce lien perdront l\'accès.')) return;
        setLoading(true);
        try {
            await axios.delete(`${API}/viewer/share`, { headers });
            setShare({ active: false });
            toast.success('Lien révoqué');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(viewerUrl);
        toast.success('Lien copié dans le presse-papier');
    };

    return (
        <>
            <Button
                variant="outline"
                onClick={() => setOpen(true)}
                className="px-5 py-6 text-base font-semibold border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10 hover:text-cyan-300"
                data-testid="viewer-share-btn"
            >
                <MonitorPlay className="w-5 h-5 mr-2" />
                Viewer
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid="viewer-share-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <MonitorPlay className="w-5 h-5 text-cyan-400" /> Mode Viewer — Partage du dashboard
                        </DialogTitle>
                        <DialogDescription>
                            Génère un lien de consultation en lecture seule, sans connexion requise.
                            Idéal pour un écran mural en salle de crise ou un partage externe (préfecture, élus, SDIS...).
                        </DialogDescription>
                    </DialogHeader>

                    {share?.active ? (
                        <div className="space-y-4">
                            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                                <p className="text-xs text-emerald-400 font-semibold flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                    Partage actif
                                </p>
                                <div className="mt-2 flex items-center gap-2">
                                    <input
                                        readOnly
                                        value={viewerUrl}
                                        className="flex-1 px-2.5 py-1.5 rounded bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs font-mono"
                                        data-testid="viewer-share-url"
                                        onFocus={(e) => e.target.select()}
                                    />
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Button size="sm" onClick={handleCopy} className="tactical-btn text-xs" data-testid="viewer-copy-btn">
                                    <Copy className="w-3.5 h-3.5 mr-1.5" /> Copier le lien
                                </Button>
                                <a href={viewerUrl} target="_blank" rel="noopener noreferrer">
                                    <Button size="sm" variant="outline" className="text-xs" data-testid="viewer-open-btn">
                                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Ouvrir dans un onglet
                                    </Button>
                                </a>
                                <Button size="sm" variant="destructive" onClick={handleRevoke} disabled={loading} className="text-xs ml-auto" data-testid="viewer-revoke-btn">
                                    <Ban className="w-3.5 h-3.5 mr-1.5" /> Révoquer
                                </Button>
                            </div>
                            <p className="text-[11px] text-zinc-500">
                                Le lien reste valable jusqu'à sa révocation. Les données affichées se rafraîchissent automatiquement toutes les 30 secondes.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-sm text-zinc-400">Aucun lien de partage actif pour le moment.</p>
                            <Button onClick={handleActivate} disabled={loading} className="tactical-btn w-full" data-testid="viewer-activate-btn">
                                <Link2 className="w-4 h-4 mr-2" /> Générer le lien de partage
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ShareViewerButton;
